import { ContentBlockSchemaRoot, JsonSchemaField } from '@/models/content-block-schema.model';
import { SupportedLocales } from '@/models/localization.model';

const isObjectMapItems = (
  items: JsonSchemaField | Record<string, JsonSchemaField> | undefined,
): items is Record<string, JsonSchemaField> =>
  !!items && typeof items === 'object' && !('type' in items && (items as JsonSchemaField).type !== undefined);

export const getArrayItemFieldMap = (schema: JsonSchemaField): Record<string, JsonSchemaField> | null => {
  if (schema.type !== 'array' || !schema.items) return null;
  if (isObjectMapItems(schema.items)) {
    return schema.items as Record<string, JsonSchemaField>;
  }
  return null;
};

export const getArrayOfStringItemsSchema = (schema: JsonSchemaField): JsonSchemaField | null => {
  if (!schema.items || typeof schema.items !== 'object') return null;
  if (Array.isArray(schema.items)) {
    const first = schema.items[0] as JsonSchemaField | undefined;
    return first?.type === 'string' ? first : null;
  }
  const node = schema.items as JsonSchemaField;
  return node.type === 'string' ? node : null;
};

export const normalizeArrayFieldSchema = (schema: JsonSchemaField): JsonSchemaField =>
  schema.type === 'array' ? schema : schema.items ? { ...schema, type: 'array' } : schema;

const buildStringDefault = (field: JsonSchemaField, locales: SupportedLocales[]): string | Record<string, string> => {
  if (field.translatable) {
    return locales.reduce<Record<string, string>>((acc, l) => {
      acc[l] = '';
      return acc;
    }, {});
  }
  return '';
};

export const buildDefaultValue = (schema: JsonSchemaField, locales: SupportedLocales[]): unknown => {
  if (schema.type === 'string') {
    return buildStringDefault(schema, locales);
  }

  if (schema.type === 'object' && schema.properties) {
    const out: Record<string, unknown> = {};
    Object.entries(schema.properties).forEach(([key, prop]) => {
      if (prop.nullable && prop.required === false) {
        return;
      }
      out[key] = buildDefaultValue(prop, locales);
    });
    return out;
  }

  if (
    schema.type === 'array' ||
    (schema.type === undefined && getArrayOfStringItemsSchema(normalizeArrayFieldSchema(schema)))
  ) {
    const asArray = normalizeArrayFieldSchema(schema);
    const stringItem = getArrayOfStringItemsSchema(asArray);
    if (stringItem) {
      if (schema.translatable) {
        return locales.reduce<Record<string, string[]>>((acc, l) => {
          acc[l] = [];
          return acc;
        }, {});
      }
      return [];
    }
    if (schema.type === 'array') {
      return [];
    }
  }

  if (schema.properties && !schema.type) {
    const out: Record<string, unknown> = {};
    Object.entries(schema.properties).forEach(([key, prop]) => {
      out[key] = buildDefaultValue(prop, locales);
    });
    return out;
  }

  return null;
};

/** Root schema may omit `type` (e.g. big_banner: only property keys at root). */
export const normalizeRootSchema = (raw: ContentBlockSchemaRoot): JsonSchemaField => {
  const r = raw as JsonSchemaField;
  if (r.type === 'array' || r.type === 'string') {
    return r;
  }
  if (r.type === 'object' && r.properties) {
    return r;
  }
  /**
   * Gallery: some payloads omit root `type` and send only `{ max_items?, items: { image, alt, link } }`.
   * Without this, the root becomes a fake `object` and `items` is rendered as list lines → unsupported.
   * List block root `items` is `{ type: 'array', items: { type: 'string' }, ... }` → isObjectMapItems is false.
   */
  if (!r.type && r.items && isObjectMapItems(r.items)) {
    return { ...r, type: 'array' };
  }
  if (!r.type) {
    return { type: 'object', properties: raw as unknown as Record<string, JsonSchemaField> };
  }
  return r;
};

/** Build defaults for block root schema from API */
export const buildDefaultBlockData = (schema: ContentBlockSchemaRoot, locales: SupportedLocales[]): unknown => {
  const root = normalizeRootSchema(schema);
  if (root.type === 'array') {
    return [];
  }
  if (root.type === 'object' && root.properties) {
    return buildDefaultValue(root, locales);
  }
  return {};
};

export const buildDefaultItemForArrayRow = (
  itemSchema: Record<string, JsonSchemaField>,
  locales: SupportedLocales[],
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  Object.entries(itemSchema).forEach(([key, prop]) => {
    out[key] = buildDefaultValue(prop, locales);
  });
  return out;
};
